import { RiFilter3Line } from "@remixicon/react";
import type {
  DisplayMinimumKey,
  DisplayMinimumView,
  DisplayMinimumWrite,
  InstrumentRef,
} from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { Field, FieldError, FieldLabel } from "@screener/uikit/components/field";
import { Input } from "@screener/uikit/components/input";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
} from "@screener/uikit/components/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { useState } from "react";
import {
  belowDaemonFloorLabel,
  CARD_MINIMUM_LOADING,
  cardMinimumCaption,
  cardMinimumTitle,
  cardMinimumTriggerLabel,
  cardMinimumValueText,
  DISPLAY_MINIMUMS_BAD_NUMBER,
  DISPLAY_MINIMUMS_LOAD_FAILED,
  DISPLAY_MINIMUMS_REJECTED,
  DISPLAY_MINIMUMS_REMOVE,
  DISPLAY_MINIMUMS_REMOVE_FAILED,
  DISPLAY_MINIMUMS_REMOVE_REJECTED,
  DISPLAY_MINIMUMS_RETRY,
  DISPLAY_MINIMUMS_SAVE,
  DISPLAY_MINIMUMS_SAVE_FAILED,
} from "@/lib/labels";
import {
  coinMinimumFor,
  type DisplayMinimumsState,
  floorForInput,
  isBelowDaemonFloor,
  parseMinimumInput,
} from "./display-minimums";
import type { DisplayMinimumOutcome, DisplayMinimumPutResult } from "./use-display-minimums";

/**
 * Значок порога в шапке карточки и окно минимума монеты (design.md
 * stage8-card-minimum, Д4). Часть экрана, не UIKit: знает про инструмент и
 * минимумы §59. Пишет ту же запись минимума монеты, что общее окно, теми же
 * `put` и `remove` из одного экземпляра `useDisplayMinimums`.
 */

/** Разбор числа поля: те же правила, что в общем окне; ошибка — текст поля, запроса нет. */
export function validateCardMinimumDraft(
  draft: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const parsed = parseMinimumInput(draft);
  return parsed.ok ? parsed : { ok: false, error: DISPLAY_MINIMUMS_BAD_NUMBER };
}

/**
 * Окно закрывается после записи, если запись из ответа сервера не ниже порога
 * скринера; ниже порога оно остаётся открытым с пометкой (§62 п. 2).
 */
export function shouldCloseAfterSave(saved: DisplayMinimumView): boolean {
  return !isBelowDaemonFloor(saved.min_usd, saved.daemon_floor_usd);
}

/**
 * Пока запрос идёт, Escape и щелчок вне окна его не закрывают: иначе
 * «закрыл, а оно сохранилось». Открытие проходит всегда.
 */
export function nextOpenState(current: boolean, requested: boolean, busy: boolean): boolean {
  return !requested && busy ? current : requested;
}

function saveFailureText(outcome: Exclude<DisplayMinimumOutcome, "ok">): string {
  return outcome === "invalid" ? DISPLAY_MINIMUMS_REJECTED : DISPLAY_MINIMUMS_SAVE_FAILED;
}

function removeFailureText(outcome: Exclude<DisplayMinimumOutcome, "ok">): string {
  return outcome === "invalid" ? DISPLAY_MINIMUMS_REMOVE_REJECTED : DISPLAY_MINIMUMS_REMOVE_FAILED;
}

/**
 * Пометка «ниже порога» для введённой суммы. Порог — из загруженного списка по
 * ключу монеты, а без её записи — по записи того же рынка (§74 п. 7); сам он
 * на клиенте не считается. Нет порога или число не разобрано — пометки нет.
 */
export function cardMinimumNote(
  draft: string,
  instrument: InstrumentRef,
  minimums: readonly DisplayMinimumView[],
): string | null {
  const parsed = parseMinimumInput(draft);
  const floorUsd = floorForInput(
    minimums,
    { exchange: instrument.exchange, market: instrument.market },
    instrument.base,
  );
  return parsed.ok && floorUsd !== null && isBelowDaemonFloor(parsed.value, floorUsd)
    ? belowDaemonFloorLabel(floorUsd)
    : null;
}

function coinKey(instrument: InstrumentRef): DisplayMinimumKey {
  return { exchange: instrument.exchange, market: instrument.market, base: instrument.base };
}

interface CardMinimumActions {
  onPut: (write: DisplayMinimumWrite) => Promise<DisplayMinimumPutResult>;
  onRemove: (key: DisplayMinimumKey) => Promise<DisplayMinimumOutcome>;
  onReload: () => void;
}

interface CardMinimumFormProps extends Omit<CardMinimumActions, "onReload"> {
  instrument: InstrumentRef;
  /** Запись минимума монеты или `null` — минимума нет. */
  minimum: DisplayMinimumView | null;
  /** Загруженный список минимумов: из него берётся порог для ввода (§74 п. 7). */
  minimums: readonly DisplayMinimumView[];
  onClose: () => void;
  /** Запрос начался или закончился: окно не закрывается Escape и щелчком, пока он идёт. */
  onBusyChange: (busy: boolean) => void;
}

/** Поле суммы, «Сохранить» и «Убрать». Блокируются только кнопки, поле и фокус остаются. */
export function CardMinimumForm({
  instrument,
  minimum,
  minimums,
  onPut,
  onRemove,
  onClose,
  onBusyChange,
}: CardMinimumFormProps) {
  const [draft, setDraft] = useState(minimum?.min_usd ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function setRequestBusy(next: boolean): void {
    setBusy(next);
    onBusyChange(next);
  }

  // Порог несохранённого ключа берётся из списка по рынку (§74 п. 7).
  const note = cardMinimumNote(draft, instrument, minimums);

  async function save(): Promise<void> {
    const checked = validateCardMinimumDraft(draft);
    if (!checked.ok) {
      setError(checked.error);
      return;
    }
    setRequestBusy(true);
    const result = await onPut({ ...coinKey(instrument), min_usd: checked.value });
    setRequestBusy(false);
    if (result.outcome === "ok") {
      setError(null);
      if (shouldCloseAfterSave(result.minimum)) {
        onClose();
      }
      return;
    }
    // Введённое число остаётся в поле: повторить можно тем же нажатием.
    setError(saveFailureText(result.outcome));
  }

  async function remove(): Promise<void> {
    setRequestBusy(true);
    const outcome = await onRemove(coinKey(instrument));
    setRequestBusy(false);
    if (outcome === "ok") {
      onClose();
      return;
    }
    setError(removeFailureText(outcome));
  }

  return (
    <form
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <Field invalid={error !== null}>
        <FieldLabel>Минимум, USD</FieldLabel>
        <Input
          inputMode="text"
          autoComplete="off"
          placeholder="USD"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
          }}
        />
        {error !== null ? <FieldError match>{error}</FieldError> : null}
      </Field>
      {note !== null ? (
        <p role="status" className="text-xs text-warning">
          {note}
        </p>
      ) : null}
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {DISPLAY_MINIMUMS_SAVE}
        </Button>
        {minimum !== null ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => void remove()}
          >
            {DISPLAY_MINIMUMS_REMOVE}
          </Button>
        ) : null}
      </div>
    </form>
  );
}

interface CardMinimumWindowProps extends CardMinimumActions {
  instrument: InstrumentRef;
  state: DisplayMinimumsState;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}

/**
 * Содержимое окна: заголовок и подпись про пары — всегда, они от списка не
 * зависят; ниже — форма, а пока список не прочитан — сообщение без поля и
 * кнопок записи (запись в непрочитанный список не ляжет).
 */
export function CardMinimumWindow({
  instrument,
  state,
  onPut,
  onRemove,
  onReload,
  onClose,
  onBusyChange,
}: CardMinimumWindowProps) {
  const lookup = coinMinimumFor(state, instrument);
  return (
    <>
      <div className="flex flex-col gap-0.5">
        <PopoverTitle>{cardMinimumTitle(instrument)}</PopoverTitle>
        <PopoverDescription className="text-xs">
          {cardMinimumCaption(instrument)}
        </PopoverDescription>
      </div>
      {state.status === "loading" ? (
        <p role="status" className="text-sm text-muted-foreground">
          {CARD_MINIMUM_LOADING}
        </p>
      ) : state.status === "unavailable" ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-sm text-destructive">
            {DISPLAY_MINIMUMS_LOAD_FAILED}
          </p>
          <Button size="sm" variant="outline" onClick={onReload}>
            {DISPLAY_MINIMUMS_RETRY}
          </Button>
        </div>
      ) : (
        <CardMinimumForm
          instrument={instrument}
          minimum={lookup.status === "read" ? lookup.minimum : null}
          minimums={state.minimums}
          onPut={onPut}
          onRemove={onRemove}
          onClose={onClose}
          onBusyChange={onBusyChange}
        />
      )}
    </>
  );
}

interface CardMinimumPopoverProps extends CardMinimumActions {
  instrument: InstrumentRef;
  state: DisplayMinimumsState;
}

/**
 * Значок порога в шапке карточки (`web-density-cards`) с окном минимума
 * монеты. Заданный минимум — плашка цвета закрепления с числом; ниже порога — предупреждающим видом с подсказкой;
 * без минимума монеты (в том числе при заданном лишь минимуме рынка) и пока
 * список не прочитан — бледный без числа. Цвет не единственный носитель
 * смысла: действие и минимум названы словами в `aria-label` и `title`.
 */
export function CardMinimumPopover({
  instrument,
  state,
  onPut,
  onRemove,
  onReload,
}: CardMinimumPopoverProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const lookup = coinMinimumFor(state, instrument);
  const minimum = lookup.status === "read" ? lookup.minimum : null;
  const belowFloor =
    minimum !== null && isBelowDaemonFloor(minimum.min_usd, minimum.daemon_floor_usd);

  const label = cardMinimumTriggerLabel(
    instrument.base,
    lookup.status === "unread"
      ? { status: "unread" }
      : minimum === null
        ? { status: "none" }
        : { status: "set", minUsd: minimum.min_usd, belowFloor },
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => setOpen((current) => nextOpenState(current, next, busy))}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-cell"
                  aria-label={label}
                  className={cn(
                    // Макет «Cards Final» (§88 п. 5): ячейка 24x22; заданный минимум —
                    // плашка с числом акцентного цвета (ширина по содержимому;
                    // решение владельца 14.10.2026), ниже порога — предупреждающая.
                    minimum === null && "text-muted-foreground",
                    minimum !== null && "w-auto gap-1 rounded-sm px-1.5",
                    minimum !== null && !belowFloor && "bg-primary/15 text-primary",
                    belowFloor && "bg-warning-wash text-warning",
                  )}
                />
              }
            />
          }
        >
          <RiFilter3Line aria-hidden="true" className={minimum === null ? undefined : "size-3"} />
          {minimum !== null ? (
            <span className="font-medium text-[11px] tabular-nums">
              {cardMinimumValueText(minimum.min_usd)}
            </span>
          ) : null}
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end">
        <CardMinimumWindow
          instrument={instrument}
          state={state}
          onPut={onPut}
          onRemove={onRemove}
          onReload={onReload}
          onClose={() => setOpen(false)}
          onBusyChange={setBusy}
        />
      </PopoverContent>
    </Popover>
  );
}
